/**
 * Leistungskatalog, Belegvorlagen und das Sammeln fertiger Prüfungen.
 *
 * Getrennt von `belege.ts`, weil es ein anderer Vorgang ist: dort wird ein
 * Dokument geschrieben, hier wird entschieden, was überhaupt abgerechnet
 * gehört. Die Entscheidung, ob eine Prüfung offen ist, trifft ausschließlich
 * der Server – die Oberfläche zählt nichts nach.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'
import type { Beleg } from './belege'
import type { Pruefart } from './typen'

// ── Leistungskatalog ────────────────────────────────────────────────

export type Leistung = {
  id: string
  bezeichnung: string
  beschreibung: string | null
  einheit: string
  einzelpreis: string
  steuersatz: string
  /** Gesetzt heißt: diese Leistung rechnet Prüfungen dieser Art ab. */
  pruefart: Pruefart | null
  sortierung: number
  aktiv: boolean
  verwendungen: number
}

export type LeistungEingabe = {
  bezeichnung: string
  beschreibung: string | null
  einheit: string
  einzelpreis: string
  steuersatz: string
  pruefart: Pruefart | null
  aktiv?: boolean
}

const schluessel = {
  leistungen: ['abrechnung', 'leistungen'] as const,
  vorlagen: ['abrechnung', 'vorlagen'] as const,
  offen: ['abrechnung', 'offen'] as const,
  offenJeKunde: (id: string) => ['abrechnung', 'offen', id] as const,
}

export function useLeistungen(rest?: { nurAktive?: boolean; enabled?: boolean }) {
  const nurAktive = rest?.nurAktive ?? true
  return useQuery({
    queryKey: [...schluessel.leistungen, nurAktive] as const,
    queryFn: ({ signal }) =>
      api.holen<Leistung[]>(`/api/abrechnung/leistungen?nur_aktive=${nurAktive}`, signal),
    enabled: rest?.enabled ?? true,
  })
}

export function useLeistungAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: LeistungEingabe) =>
      api.senden<Leistung>('/api/abrechnung/leistungen', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel.leistungen }),
  })
}

export function useLeistungAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<LeistungEingabe>) =>
      api.aendern<Leistung>(`/api/abrechnung/leistungen/${id}`, daten),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: schluessel.leistungen })
      // Vorlagen verweisen auf den Katalog – ihre Summe ändert sich mit.
      void speicher.invalidateQueries({ queryKey: schluessel.vorlagen })
    },
  })
}

export function useLeistungLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/abrechnung/leistungen/${id}`),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: schluessel.leistungen })
      void speicher.invalidateQueries({ queryKey: schluessel.vorlagen })
    },
  })
}

// ── Vorlagen ────────────────────────────────────────────────────────

export type Vorlagenposition = {
  id: string
  leistung_id: string
  position: number
  menge: string
  bezeichnung: string
  einheit: string
  einzelpreis: string
}

export type Vorlage = {
  id: string
  name: string
  art: string
  betreff: string | null
  einleitung: string | null
  schluss: string | null
  sortierung: number
  aktiv: boolean
  positionen: Vorlagenposition[]
  netto: string
}

export function useVorlagen(rest?: { enabled?: boolean }) {
  return useQuery({
    queryKey: schluessel.vorlagen,
    queryFn: ({ signal }) => api.holen<Vorlage[]>('/api/abrechnung/vorlagen', signal),
    enabled: rest?.enabled ?? true,
  })
}

export function useVorlageAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: { name: string; betreff?: string | null; einleitung?: string | null; schluss?: string | null }) =>
      api.senden<Vorlage>('/api/abrechnung/vorlagen', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel.vorlagen }),
  })
}

export function useVorlageAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<Vorlage>) =>
      api.aendern<Vorlage>(`/api/abrechnung/vorlagen/${id}`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel.vorlagen }),
  })
}

export function useVorlageLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/abrechnung/vorlagen/${id}`),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel.vorlagen }),
  })
}

export function useVorlagenposition() {
  const speicher = useQueryClient()
  const fertig = () => speicher.invalidateQueries({ queryKey: schluessel.vorlagen })

  return {
    anlegen: useMutation({
      mutationFn: ({ vorlageId, ...daten }: { vorlageId: string; leistung_id: string; menge?: string }) =>
        api.senden<Vorlage>(`/api/abrechnung/vorlagen/${vorlageId}/positionen`, daten),
      onSuccess: fertig,
    }),
    aendern: useMutation({
      mutationFn: ({ vorlageId, id, ...daten }: { vorlageId: string; id: string; menge?: string }) =>
        api.aendern<Vorlage>(`/api/abrechnung/vorlagen/${vorlageId}/positionen/${id}`, daten),
      onSuccess: fertig,
    }),
    loeschen: useMutation({
      mutationFn: ({ vorlageId, id }: { vorlageId: string; id: string }) =>
        api.loeschen(`/api/abrechnung/vorlagen/${vorlageId}/positionen/${id}`),
      onSuccess: fertig,
    }),
  }
}

// ── Sammeln ─────────────────────────────────────────────────────────

export type OffenerKunde = {
  kunde_id: string
  kunde_name: string
  anzahl: number
  von: string
  bis: string
}

export type OffenePruefung = {
  pruefung_id: string
  spielplatz_id: string
  spielplatz_name: string
  ort: string | null
  pruefart: Pruefart
  datum: string
  berichtsnummer: string | null
  /** `null` heißt: für diese Prüfart steht nichts im Katalog. */
  vorschlag_bezeichnung: string | null
  vorschlag_einzelpreis: string | null
}

export function useOffeneKunden(rest?: { enabled?: boolean }) {
  return useQuery({
    queryKey: schluessel.offen,
    queryFn: ({ signal }) => api.holen<OffenerKunde[]>('/api/abrechnung/offen', signal),
    enabled: rest?.enabled ?? true,
  })
}

export function useOffenePruefungen(kundeId: string | null) {
  return useQuery({
    queryKey: schluessel.offenJeKunde(kundeId ?? 'keiner'),
    queryFn: ({ signal }) =>
      api.holen<OffenePruefung[]>(`/api/abrechnung/offen/${kundeId}`, signal),
    enabled: Boolean(kundeId),
  })
}

export function useSammeln() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      kunde_id: string
      pruefung_ids: string[]
      betreff?: string | null
    }) => api.senden<Beleg>('/api/abrechnung/sammeln', daten),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: schluessel.offen })
      void speicher.invalidateQueries({ queryKey: ['belege'] })
    },
  })
}

// ── Am Beleg ────────────────────────────────────────────────────────

export function useAusKatalog(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: { leistung_id: string; menge?: string }) =>
      api.senden<Beleg>(`/api/belege/${belegId}/positionen/aus-katalog`, daten),
    onSuccess: (neu) => {
      speicher.setQueryData(['beleg', belegId], neu)
      void speicher.invalidateQueries({ queryKey: schluessel.leistungen })
    },
  })
}

export function useVorlageEinsetzen(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: { vorlage_id: string; texte_uebernehmen?: boolean }) =>
      api.senden<Beleg>(`/api/belege/${belegId}/vorlage`, daten),
    onSuccess: (neu) => speicher.setQueryData(['beleg', belegId], neu),
  })
}

export type VersandErgebnis = {
  verschickt: boolean
  an: string
  meldung: string
  /** Lag die XRechnung als XML mit im Anhang? */
  xrechnung: boolean
  /** Wenn nicht: was dafür fehlt. Leer heißt, dass keine vorgesehen war. */
  xrechnung_fehlt: string[]
}

export function useBelegVersenden(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: { an?: string | null; text?: string | null }) =>
      api.senden<VersandErgebnis>(`/api/belege/${belegId}/versenden`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['kundenakte'] }),
  })
}
